// Firma kullanıcısı girişte: hedeflenen duyurular sırayla açılır pencere (onaylılar önce)
import { useEffect, useState } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { X, Megaphone, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { AnnouncementAttachments } from './AnnouncementAttachments';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FirmAnnouncementPopup = ({ enabled }) => {
  const [queue, setQueue] = useState([]);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const cur = queue[0];

  useEffect(() => {
    if (!enabled) return;
    const load = () => axios.get(`${API}/firm/announcements`).then(r => setQueue(r.data)).catch(() => {});
    load();
    // Onaylanmamış duyuru nedeniyle engellenen işlemde (423) pencereyi yeniden aç
    const id = axios.interceptors.response.use(null, (err) => { if (err.response?.status === 423) load(); return Promise.reject(err); });
    window.addEventListener('announcements:refresh', load);
    return () => { axios.interceptors.response.eject(id); window.removeEventListener('announcements:refresh', load); };
  }, [enabled]);
  useEffect(() => {
    if (!cur || !enabled) return;
    setChecked(false);
    axios.post(`${API}/firm/announcements/${cur.id}/seen`).catch(() => {});
  }, [cur?.id, enabled]);

  if (!cur || !enabled) return null;
  const next = () => setQueue(q => q.slice(1));
  const ack = async () => {
    setBusy(true);
    try { await axios.post(`${API}/firm/announcements/${cur.id}/ack`); next(); window.dispatchEvent(new Event('announcements:count')); } catch { /* tekrar denenebilir */ }
    setBusy(false);
  };
  const isAck = cur.mode === 'ack';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in" data-testid="firm-announcement-popup">
      <div className="relative w-full max-w-xl rounded-2xl bg-white shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-4">
        {!isAck && (
          <button type="button" onClick={next} className="absolute right-3 top-3 rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors" data-testid="firm-announcement-close" aria-label="Kapat">
            <X className="h-5 w-5" />
          </button>
        )}
        <div className={`flex items-center gap-3 rounded-t-2xl px-6 py-4 ${isAck ? 'bg-rose-50' : 'bg-sky-50'}`}>
          {isAck ? <ShieldCheck className="h-6 w-6 text-rose-600" /> : <Megaphone className="h-6 w-6 text-sky-600" />}
          <div className="min-w-0 pr-8">
            <p className="text-lg font-semibold text-slate-900" data-testid="firm-announcement-title">{cur.title}</p>
            {cur.block_permits && <p className="text-xs font-medium text-rose-700" data-testid="firm-announcement-block-note">Bu duyuruyu onaylamadan yeni talep oluşturamazsınız.</p>}
            {queue.length > 1 && <p className="text-xs text-slate-500">{queue.length - 1} duyuru daha var</p>}
          </div>
        </div>
        <div className="announcement-content max-h-[55vh] overflow-y-auto px-6 py-4 text-sm text-slate-700" data-testid="firm-announcement-body"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cur.html || '', { ADD_ATTR: ['target'] }) }} />
        {cur.attachments?.length > 0 && <div className="px-6 pb-4"><AnnouncementAttachments aid={cur.id} attachments={cur.attachments} /></div>}
        <div className="flex flex-wrap items-center gap-3 border-t px-6 py-4">
          {isAck ? (
            <>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-800 cursor-pointer">
                <Checkbox checked={checked} onCheckedChange={(v) => setChecked(!!v)} data-testid="firm-announcement-ack-check" />Okudum ve anladım
              </label>
              <Button className="ml-auto bg-rose-600 hover:bg-rose-700" disabled={!checked || busy} onClick={ack} data-testid="firm-announcement-ack-btn">Onaylıyorum</Button>
            </>
          ) : <Button className="ml-auto" variant="outline" onClick={next} data-testid="firm-announcement-ok-btn">Kapat</Button>}
        </div>
      </div>
    </div>
  );
};
